import React from 'react'
import {Card, Button, Pagination} from '@therms/atalaya'
import {XMarkIcon} from '@heroicons/react/24/solid'
import {UserPosition} from '@src/modules/Messages/shared/UserPosition'
import {UserName} from '@src/modules/Users/shared/UserName'
import dayjs from 'dayjs'
import {Thread} from '@src/generatedClient/generated/taskreportsApi'

interface User {
  id: string | number;
  name: string;
}
interface ThreadListPanelData {
  threads?: Thread[];
  total?: number;
}
interface ThreadListPanelProps {
  isSelectingUser: boolean;
  users: User[];
  handleSelectUser: (user: User) => void;
  handleCancelSelectUser: () => void;
  data?: ThreadListPanelData;
  selectedThreadId?: string | number;
  handleSelectMessage: (thread: Thread) => void;
  recordsPerPage: number;
  offset: number;
  setOffset: (offset: number) => void;
}

function ThreadListPanel({
  isSelectingUser,
  users,
  handleSelectUser,
  handleCancelSelectUser,
  data,
  selectedThreadId,
  handleSelectMessage,
  recordsPerPage,
  offset,
  setOffset
}: ThreadListPanelProps) {
  let listContent
  if (isSelectingUser) {
    if (users.length === 0) {
      listContent = <div className="text-center text-gray-400 py-6">No users found.</div>
    } else {
      listContent = users.map((user) => (
        <div
          key={user.id}
          className="flex justify-between items-center px-4 py-4 cursor-pointer border-b border-gray-200 bg-white hover:bg-blue-50 transition rounded-lg"
          onClick={() => handleSelectUser(user)}
        >
          <div className="w-3/4 flex flex-col gap-1">
            <div className="font-semibold">{user.name}</div>
            <div className="flex gap-1">
              <div className="flex items-center bg-gray-200 px-4 py-1 rounded-md text-sm">
                <UserPosition userId={String(user.id)} />
              </div>
            </div>
          </div>
        </div>
      ))
    }
  } else if (data?.threads && data?.threads.length === 0) {
    listContent = <div className="text-center text-gray-400 py-6">No threads found.</div>
  } else if (data?.threads) {
    listContent = data.threads.map((thread) => (
      <div
        key={thread.id}
        className={`flex justify-between items-center px-4 py-4 cursor-pointer border-b border-gray-200 ${thread.unreadByAdmin ? 'bg-gray-300' : 'bg-gray-50'} ${selectedThreadId === thread.id ? '!bg-blue-100' : ''}`}
        onClick={() => handleSelectMessage(thread)}
      >
        <div className="w-3/4 flex flex-col gap-1">
          <div className="font-semibold"><UserName id={thread.userId} /></div>
          <div className="flex gap-1">
            <div className="flex items-center bg-gray-200 px-4 py-1 rounded-md text-sm">
              <UserPosition userId={thread.userId} />
            </div>
          </div>
          <div className="text-sm text-gray-500">{thread.lastMessage}</div>
        </div>
        <div className="flex items-center w-1/4 justify-end gap-2">
          <div className="text-sm text-gray-500">{dayjs(thread.cts).fromNow()}</div>
          {thread.unreadByAdmin && (
            <div className="w-3 h-3 rounded-full bg-green-500" />
          )}
        </div>
      </div>
    ))
  }

  return (
    <Card className="px-0 py-1 border border-gray-200 rounded-lg h-full">
      <div className="flex px-4 py-2 h-7 items-center justify-between">
        <div className="w-3/4">
          <div className="font-semibold">
            {isSelectingUser ? 'Select User' : 'User Messages'}
          </div>
        </div>
        <div className="w-1/4 text-right mt-2">
          {isSelectingUser && (
            <Button variant="main" className="flex items-center gap-1 text-gray-600 rounded-md" onClick={handleCancelSelectUser}>
              <XMarkIcon className="w-5 h-5" /> Cancel
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1 overflow-y-auto">
        {listContent}
      </div>
      {!isSelectingUser && (
        <Pagination
          recordsPerPage={recordsPerPage}
          skippedRecords={offset}
          onPageClick={setOffset}
          showDescription={false}
          totalRecords={data?.total || 0}
        />
      )}
    </Card>
  )
}

export default ThreadListPanel
